import { Form } from 'antd';
import { useState } from 'react';
import { AppButton } from 'shared/UI/UIKit/Button';
import { AppFlex } from 'shared/UI/UIKit/Flex';
import { AppForm, AppFormItem } from 'shared/UI/UIKit/Form';
import { AppInputNumber } from 'shared/UI/UIKit/InputNumber';
import { AppModal } from 'shared/UI/UIKit/Modal';
import { AppMultipleSelect } from 'shared/UI/UIKit/Select';
import { AppText } from 'shared/UI/UIKit/Text';
import { useGenerateClassGroupsMutation } from 'shared/api';
import type { AppSelectOption } from 'shared/UI/UIKit/Select';
import type { GenerateClassGroupsResponse } from 'shared/api';
import styles from './styles.module.scss';

type GenerateClassGroupsFormValues = {
  educationalClassIds: number[];
  subjectIds: number[];
  count: number;
};

type GenerateClassGroupsModalProps = {
  visible: boolean;
  classOptions: AppSelectOption[];
  subjectOptions: AppSelectOption[];
  getClassName: (id: number) => string;
  getSubjectName: (id: number) => string;
  onClose: () => void;
};

export const GenerateClassGroupsModal = ({
  visible,
  classOptions,
  subjectOptions,
  getClassName,
  getSubjectName,
  onClose,
}: GenerateClassGroupsModalProps) => {
  const [form] = Form.useForm<GenerateClassGroupsFormValues>();
  const [generateClassGroups, { isLoading }] = useGenerateClassGroupsMutation();
  const [result, setResult] = useState<GenerateClassGroupsResponse | null>(null);

  const close = () => {
    form.resetFields();
    setResult(null);
    onClose();
  };

  const onSubmit = async () => {
    const { educationalClassIds, subjectIds, count } = await form.validateFields();

    setResult(await generateClassGroups({ educationalClassIds, subjectIds, count }).unwrap());
  };

  const footer = result ? (
    <AppButton onClick={close}>Закрыть</AppButton>
  ) : (
    <AppFlex direction="row">
      <AppButton onClick={onSubmit}>Создать группы</AppButton>
      <AppButton type="outline" onClick={close}>
        Отмена
      </AppButton>
    </AppFlex>
  );

  return (
    <AppModal
      visible={visible}
      maskClosable={false}
      onCancel={close}
      destroyOnClose
      loading={isLoading}
      title="Массовое создание учебных групп"
      footer={footer}
      content={
        result ? (
          <AppFlex>
            <AppText size={18}>Создано групп: {result.created.length}</AppText>
            <AppText size={18}>Пропущено пар: {result.skipped.length}</AppText>
            {result.skipped.map((pair) => (
              <AppText key={`${pair.educationalClassId}-${pair.subjectId}`} type="secondary">
                {getClassName(pair.educationalClassId)} — {getSubjectName(pair.subjectId)}
                {pair.reason ? `: ${pair.reason}` : ''}
              </AppText>
            ))}
          </AppFlex>
        ) : (
          <AppForm form={form} initialValues={{ count: 2 }}>
            <AppFormItem
              name="educationalClassIds"
              label="Классы"
              rules={[{ required: true, message: 'Выберите хотя бы один класс' }]}
            >
              <AppMultipleSelect
                className={styles.multipleSelect}
                showSearch
                optionFilterProp="label"
                options={classOptions}
                placeholder="Выберите классы"
              />
            </AppFormItem>
            <AppFormItem
              name="subjectIds"
              label="Предметы"
              rules={[{ required: true, message: 'Выберите хотя бы один предмет' }]}
            >
              <AppMultipleSelect
                className={styles.multipleSelect}
                showSearch
                optionFilterProp="label"
                options={subjectOptions}
                placeholder="Выберите предметы"
              />
            </AppFormItem>
            <AppFormItem
              name="count"
              label="Количество групп"
              rules={[
                { required: true, message: 'Укажите количество групп' },
                {
                  type: 'number',
                  min: 1,
                  max: 10,
                  message: 'Количество групп должно быть от 1 до 10',
                },
              ]}
            >
              <AppInputNumber min={1} max={10} precision={0} />
            </AppFormItem>
          </AppForm>
        )
      }
    />
  );
};
